"use client";

import {
	TAB_HEARTBEAT_EXPIRY_SECONDS,
	TAB_IDENTITY_LOCK_PREFIX,
	TAB_IDENTITY_STORAGE_KEY,
} from "@klarify/constants";
import { api } from "@klarify/ui/trpc/client";
import {
	durationToMilliseconds,
	LocalStorageMap,
} from "@klarify/utilities/client";
import { differenceInMilliseconds } from "date-fns";
import { createContext, useContext, useEffect, useState } from "react";

const HEARTBEAT_EXPIRY_MS = durationToMilliseconds(
	TAB_HEARTBEAT_EXPIRY_SECONDS,
);
const HEARTBEAT_UPDATE_INTERVAL_MS = 5000;
const EXPIRED_TAB_POLL_INTERVAL_MS = 2 * HEARTBEAT_UPDATE_INTERVAL_MS;

const tabHeartbeats = new LocalStorageMap<string, Date>("klarifyTabIdsV2");

export function isTabActive(tabId: string): boolean {
	const lastHeartbeat = tabHeartbeats.get(tabId);
	if (!lastHeartbeat) {
		return false;
	}
	return Date.now() - lastHeartbeat.getTime() < HEARTBEAT_EXPIRY_MS;
}

function readPersistedTabId(): string | null {
	try {
		return sessionStorage.getItem(TAB_IDENTITY_STORAGE_KEY);
	} catch {
		return null;
	}
}

function persistTabId(tabId: string) {
	try {
		sessionStorage.setItem(TAB_IDENTITY_STORAGE_KEY, tabId);
	} catch {
		// Losing persistence only costs identity resumption on the next reload.
	}
}

function getLockManager(): LockManager | null {
	if (typeof navigator === "undefined" || !("locks" in navigator)) {
		return null;
	}
	return navigator.locks || null;
}

/**
 * One request per lock name for the life of this document. React StrictMode
 * mounts effects twice, and a second request for a name this document already
 * holds would report "held elsewhere" — reading our own lock as a duplicate
 * tab.
 */
const identityLockRequests = new Map<string, Promise<boolean>>();

/**
 * Hold `name` until this document dies. Resolves true once held, false when
 * another live document already holds it.
 */
function tryHoldIdentityLock(name: string): Promise<boolean> {
	const existing = identityLockRequests.get(name);
	if (existing) {
		return existing;
	}
	const locks = getLockManager();
	if (!locks) {
		return Promise.resolve(false);
	}
	const request = new Promise<boolean>((resolve) => {
		locks
			.request(name, { ifAvailable: true }, (lock) => {
				if (!lock) {
					resolve(false);
					return;
				}
				resolve(true);
				// Never settles: the browser releases the lock only when this
				// document is destroyed, which is exactly the signal a reload
				// uses to prove the previous incarnation is gone.
				return new Promise<never>(() => {});
			})
			.catch(() => resolve(false));
	});
	identityLockRequests.set(name, request);
	return request;
}

/**
 * Decide this document's tab identity. A reloaded (or restored) tab resumes
 * the identity it persisted, so the server keeps treating it as the owner of
 * any recording it started. Without this, a mid-recording refresh left the
 * session bound to a dead tab id, and every repair answered "active in
 * another tab" until that registration expired (~5-10 minutes).
 *
 * The Web Lock is what makes resumption safe: Chrome's Duplicate Tab copies
 * sessionStorage, but the original still holds the lock, so the copy fails
 * the claim and mints its own identity. Without the Locks API a persisted
 * identity cannot be proven exclusive, so the old fresh-identity-per-document
 * behaviour is kept.
 */
async function claimTabIdentity(candidate: string): Promise<string> {
	if (!getLockManager()) {
		return candidate;
	}
	if (await tryHoldIdentityLock(TAB_IDENTITY_LOCK_PREFIX + candidate)) {
		persistTabId(candidate);
		return candidate;
	}
	const fresh = crypto.randomUUID();
	await tryHoldIdentityLock(TAB_IDENTITY_LOCK_PREFIX + fresh);
	persistTabId(fresh);
	return fresh;
}

/**
 * The adoption verdict is a fact about the DOCUMENT, decided exactly once at
 * its first identity claim. Without this latch a remount of `TabIdProvider`
 * (locale switch, error-boundary reset, Suspense retry) would re-derive
 * "adopted" from its own leftovers: `persistTabId` already wrote the id and
 * `identityLockRequests` memoizes the held lock as claimable, so the remount
 * looks exactly like a resurrection — with no dead predecessor. Latching the
 * first verdict keeps the invariant "adopted ⇒ a previous incarnation of
 * this tab died" true for the document's whole lifetime. `null` means no
 * claim has resolved yet.
 */
let documentAdoptionVerdict: boolean | null = null;

function resolveDocumentAdoptionVerdict(adoptedNow: boolean) {
	if (documentAdoptionVerdict === null) {
		documentAdoptionVerdict = adoptedNow;
	}
	return documentAdoptionVerdict;
}

/** Test-only: the verdict is per-document, and bun tests share one document. */
export function resetDocumentAdoptionVerdictForTesting() {
	documentAdoptionVerdict = null;
}

const TabIdContext = createContext<string>("");

/**
 * True once this document has proven it RESUMED a persisted identity: the
 * candidate came from sessionStorage and the exclusive lock for it was free.
 * That combination means a previous incarnation of this tab existed and is
 * dead — the lock only releases on document destruction — which is the one
 * situation where an observer may treat that incarnation's leftovers
 * (activity markers, upload progress, an orphaned recording) as provably
 * abandoned rather than merely quiet. A duplicate tab copies sessionStorage
 * but finds the lock held, so it never reads as adopted; a fresh tab has
 * nothing persisted; without Web Locks nothing is ever adopted.
 */
const AdoptedTabIdentityContext = createContext(false);

export function TabIdProvider({ children }: { children: React.ReactNode }) {
	// Optimistic: reuse the persisted identity immediately so the common case
	// (a reload) never observes an interim id. The async claim below only
	// swaps it for a duplicate tab, whose original still holds the lock.
	// Without Web Locks the persisted identity must not be adopted at all —
	// registering a dead tab's id would extend its server-side liveness and
	// lengthen the very wall this exists to remove.
	const [initialIdentity] = useState(() => {
		if (!getLockManager()) {
			return { candidate: crypto.randomUUID(), persisted: false };
		}
		const persistedTabId = readPersistedTabId();
		if (persistedTabId) {
			return { candidate: persistedTabId, persisted: true };
		}
		return { candidate: crypto.randomUUID(), persisted: false };
	});
	const [currentTabId, setCurrentTabId] = useState(initialIdentity.candidate);
	const [adoptedIdentity, setAdoptedIdentity] = useState(false);

	useEffect(() => {
		let cancelled = false;
		void claimTabIdentity(initialIdentity.candidate).then((claimedTabId) => {
			const adopted = resolveDocumentAdoptionVerdict(
				initialIdentity.persisted && claimedTabId === initialIdentity.candidate,
			);
			if (!cancelled) {
				setCurrentTabId(claimedTabId);
				setAdoptedIdentity(adopted);
			}
		});
		return () => {
			cancelled = true;
		};
	}, [initialIdentity]);

	const registerTabMutation = api.recordingRouter.v2.registerTab.useMutation();
	const deregisterTabMutation =
		api.recordingRouter.v2.deregisterTab.useMutation({
			onSuccess: (_data, variables) => {
				tabHeartbeats.delete(variables.tabId);
			},
		});

	// Register tab on mount and periodically refresh the server-side heartbeat
	useEffect(() => {
		registerTabMutation.mutate({ tabId: currentTabId });
		const intv = setInterval(() => {
			registerTabMutation.mutate({ tabId: currentTabId });
		}, HEARTBEAT_EXPIRY_MS);
		return () => {
			clearInterval(intv);
		};
	}, [currentTabId, registerTabMutation.mutate]);

	// Update the local heartbeat timestamp frequently so other tabs can detect liveness
	useEffect(() => {
		tabHeartbeats.set(currentTabId, new Date());
		const intv = setInterval(() => {
			tabHeartbeats.set(currentTabId, new Date());
		}, HEARTBEAT_UPDATE_INTERVAL_MS);
		return () => {
			clearInterval(intv);
		};
	}, [currentTabId]);

	// Poll for expired tabs and deregister them
	useEffect(() => {
		const removeExpiredTabs = () => {
			for (const [tabId, lastHeartbeat] of tabHeartbeats) {
				if (
					differenceInMilliseconds(new Date(), lastHeartbeat) >=
					HEARTBEAT_EXPIRY_MS
				) {
					deregisterTabMutation.mutate({ tabId });
				}
			}
		};
		removeExpiredTabs();
		const intv = setInterval(removeExpiredTabs, EXPIRED_TAB_POLL_INTERVAL_MS);
		return () => {
			clearInterval(intv);
		};
	}, [deregisterTabMutation.mutate]);

	return (
		<TabIdContext.Provider value={currentTabId}>
			<AdoptedTabIdentityContext.Provider value={adoptedIdentity}>
				{children}
			</AdoptedTabIdentityContext.Provider>
		</TabIdContext.Provider>
	);
}

export function useTabId() {
	const value = useContext(TabIdContext);
	if (value === "") {
		throw new Error("useTabId must be used within a TabIdProvider");
	}
	return value;
}

/**
 * Whether this tab resumed a dead predecessor's identity. See
 * `AdoptedTabIdentityContext` for exactly what that proves. Defaults to false
 * outside the provider so read-only consumers never throw.
 */
export function useAdoptedTabIdentity() {
	return useContext(AdoptedTabIdentityContext);
}
